import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import {
  Check, ChevronLeft, ChevronRight, Plus, Trash2, Star, Eye, Upload, GripVertical, Languages, Sparkles, MapPin, Loader2,
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import AdminLayout from "@/components/admin/AdminLayout";
import PropertyMap from "@/components/PropertyMap";
import { iconFor, amenitiesByCategory, amenityLabel } from "@/lib/amenities";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STEPS = ["Proprietario", "Posizione", "Servizi", "Informazioni", "Foto", "Booking", "SEO", "Traduzioni & Pubblica"];

const SUGGESTED_IMAGES = [
  "https://images.unsplash.com/photo-1756726597936-199d97b85e3f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjBpdGFsaWFuJTIwdmlsbGElMjB0dXNjYW55JTIwcG9vbHxlbnwwfHx8fDE3ODkxMzYxNDZ8MA&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1678902177251-7ec8fea483a3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2ODl8MHwxfHNlYXJjaHwyfHxsdXh1cnklMjBpdGFsaWFuJTIwdmlsbGElMjB0dXNjYW55JTIwcG9vbHxlbnwwfHx8fDE3ODkxMzYxNDZ8MA&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1744731058072-0ba2c1623474?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwzfHxjb3p5JTIwaXRhbGlhbiUyMHZhY2F0aW9uJTIwYXBhcnRtZW50JTIwaW50ZXJpb3J8ZW58MHx8fHwxNzg5MTM2MTQ2fDA&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1744731057719-1be0abbdc3c7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwxfHxjb3p5JTIwaXRhbGlhbiUyMHZhY2F0aW9uJTIwYXBhcnRtZW50JTIwaW50ZXJpb3J8ZW58MHx8fHwxNzg5MTM2MTQ2fDA&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1726136056898-717ca659a1ec?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzd8MHwxfHNlYXJjaHw0fHxpdGFsaWFuJTIwbGFrZSUyMHZpZXclMjB2YWNhdGlvbiUyMGhvdXNlJTIwYW1hbGZpfGVufDB8fHx8MTc4OTEzNjE0Nnww&ixlib=rb-4.1.0&q=85",
  "https://images.unsplash.com/photo-1744731057723-f12c74ad0bcb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHw0fHxjb3p5JTIwaXRhbGlhbiUyMHZhY2F0aW9uJTIwYXBhcnRtZW50JTIwaW50ZXJpb3J8ZW58MHx8fHwxNzg5MTM2MTQ2fDA&ixlib=rb-4.1.0&q=85",
];

const DEFAULT = {
  name: "", subtitle: "", type: "Appartamento", status: "bozza", owner_id: "", cin: "",
  city: "", address: "", province: "", region: "", cap: "", neighborhood: "",
  lat: 41.9, lng: 12.5, show_exact_location: false,
  show_owner_contacts: false,
  guests: 2, bedrooms: 1, beds: 1, bathrooms: 1, sqm: "",
  description: { presentation: "", spaces: "", neighborhood: "", other: "" },
  amenities: [], photos: [], price_per_night: 100, cleaning_fee: 0, min_stay: 1, discount_percent: 0,
  rules: { checkin_from: "", checkin_to: "", checkout_by: "", no_smoking: false, no_pets: false, self_checkin: false, notify_host: false, linen_change_extra: false, cleaning_extra: false, weekly_cleaning: false },
  booking_engine: { type: "link", embed_code: "", url: "", widget_id: "" },
  seo: { seo_title: "", meta_description: "", h1: "", intro_text: "", og_image: "" }, badge: "", nearby_places: [], translations: {},
};

export default function PropertyWizard() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  const [step, setStep] = useState(0);
  const [form, setForm] = useState(DEFAULT);
  const [owners, setOwners] = useState([]);
  const [saving, setSaving] = useState(false);
  const [newPhoto, setNewPhoto] = useState("");
  const [uploading, setUploading] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);
  const [transLang, setTransLang] = useState("en");
  const [translating, setTranslating] = useState(false);
  const [aiBusy, setAiBusy] = useState("");
  const [aiPhotos, setAiPhotos] = useState([]);
  const [loaded, setLoaded] = useState(!isEdit);
  const cur = STEPS[step];

  useEffect(() => {
    api.get("/admin/owners").then((r) => setOwners(r.data));
    if (isEdit) {
      api.get(`/admin/properties/${id}`).then((r) => {
        const d = r.data;
        setForm({ ...DEFAULT, ...d, description: { ...DEFAULT.description, ...(d.description || {}) }, rules: { ...DEFAULT.rules, ...(d.rules || {}) }, booking_engine: { ...DEFAULT.booking_engine, ...(d.booking_engine || {}) }, seo: { ...DEFAULT.seo, ...(d.seo || {}) } });
        setLoaded(true);
      }).catch(() => toast.error("Struttura non trovata"));
    }
  }, [id, isEdit]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setDesc = (k, v) => setForm((f) => ({ ...f, description: { ...f.description, [k]: v } }));
  const setSeo = (k, v) => setForm((f) => ({ ...f, seo: { ...f.seo, [k]: v } }));
  const setBE = (k, v) => setForm((f) => ({ ...f, booking_engine: { ...f.booking_engine, [k]: v } }));
  const setRule = (k, v) => setForm((f) => ({ ...f, rules: { ...(f.rules || {}), [k]: v } }));

  const toggleAmenity = (key) => set("amenities", form.amenities.includes(key) ? form.amenities.filter((a) => a !== key) : [...form.amenities, key]);

  const addNearby = () => set("nearby_places", [...(form.nearby_places || []), { name: "", category: "attrazione", distance: "", lat: null, lng: null }]);
  const updateNearby = (i, k, v) => set("nearby_places", form.nearby_places.map((n, idx) => idx === i ? { ...n, [k]: v } : n));
  const removeNearby = (i) => set("nearby_places", form.nearby_places.filter((_, idx) => idx !== i));

  const getTr = (l) => form.translations?.[l] || {};
  const setTr = (l, field, val) => setForm((f) => ({ ...f, translations: { ...f.translations, [l]: { ...(f.translations?.[l] || {}), [field]: val } } }));
  const setTrDesc = (l, k, val) => setForm((f) => { const cur = f.translations?.[l] || {}; return { ...f, translations: { ...f.translations, [l]: { ...cur, description: { ...(cur.description || {}), [k]: val } } } }; });
  const setTrNearby = (l, i, val) => setForm((f) => { const cur = f.translations?.[l] || {}; const arr = [...(cur.nearby_names || [])]; arr[i] = val; return { ...f, translations: { ...f.translations, [l]: { ...cur, nearby_names: arr } } }; });
  const setTrSeo = (l, k, val) => setForm((f) => { const cur = f.translations?.[l] || {}; return { ...f, translations: { ...f.translations, [l]: { ...cur, seo: { ...(cur.seo || {}), [k]: val } } } }; });

  const brief = () => ({ ...form, photos: undefined, translations: undefined, amenity_labels: form.amenities.map((k) => amenityLabel(k, "it")) });

  const runAi = async (kind, fn) => {
    setAiBusy(kind);
    try { await fn(); } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Servizio AI non disponibile"); } finally { setAiBusy(""); }
  };

  const fetchNearby = async (lat, lng) => {
    const { data } = await api.post("/admin/ai/nearby", { lat, lng, city: form.city });
    const fresh = data.map(({ estimated, ...n }) => n);
    setForm((f) => {
      const names = new Set((f.nearby_places || []).map((n) => n.name.toLowerCase()));
      return { ...f, nearby_places: [...(f.nearby_places || []), ...fresh.filter((n) => !names.has(n.name.toLowerCase()))] };
    });
    if (data.some((n) => n.estimated)) toast.warning(`${data.length} luoghi suggeriti: distanze stimate, verificale`);
    else toast.success(`${data.length} luoghi suggeriti con le distanze`);
  };
  const findNearby = () => runAi("nearby", () => fetchNearby(form.lat, form.lng));

  const geocode = () => runAi("geo", async () => {
    if (!form.address || !form.city) { toast.error("Inserisci indirizzo e città"); return; }
    const { data } = await api.post("/admin/ai/geocode", { address: form.address, city: form.city, cap: form.cap, province: form.province });
    setForm((f) => ({ ...f, lat: data.lat, lng: data.lng, province: f.province || data.province, region: f.region || data.region, cap: f.cap || data.cap, neighborhood: f.neighborhood || data.neighborhood }));
    if (data.approx) toast.warning("Posizione approssimativa: controlla il punto sulla mappa e correggi le coordinate se serve"); else toast.success("Posizione rilevata sulla mappa");
    if (!(form.nearby_places || []).length) {
      setAiBusy("nearby");
      await fetchNearby(data.lat, data.lng);
    }
  });

  const suggestDescription = () => runAi("desc", async () => {
    const { data } = await api.post("/admin/ai/describe", { property: brief() });
    setForm((f) => ({ ...f, subtitle: f.subtitle || data.subtitle || "", description: { presentation: data.presentation || "", spaces: data.spaces || "", neighborhood: data.neighborhood || "", other: data.other || "" } }));
    toast.success("Descrizioni suggerite: rileggile e modificale se serve");
  });

  const suggestSeo = () => runAi("seo", async () => {
    const { data } = await api.post("/admin/ai/seo", { property: brief() });
    setForm((f) => ({ ...f, slug: f.slug || data.slug || "", seo: { ...f.seo, seo_title: data.seo_title || "", meta_description: data.meta_description || "", h1: data.h1 || "", intro_text: data.intro_text || "" } }));
    toast.success("Campi SEO suggeriti");
  });

  useEffect(() => {
    if (!loaded || aiBusy) return;
    if (cur === "Informazioni" && !Object.values(form.description || {}).some(Boolean)) suggestDescription();
    if (cur === "SEO" && !["seo_title", "meta_description", "h1", "intro_text"].some((k) => form.seo?.[k])) suggestSeo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cur, loaded]);

  const newPhoto_ = (url, f) => ({ id: `ph-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`, url, title: "", alt: f.name, order: f.photos.length, is_main: f.photos.length === 0, category: "" });
  const describePhotos = async (list) => {
    for (const p of list) {
      setAiPhotos((a) => [...a, p.id]);
      try {
        const { data } = await api.post("/admin/ai/photo-meta", { url: p.url, name: form.name, type: form.type, city: form.city });
        setForm((f) => ({ ...f, photos: f.photos.map((x) => (x.id === p.id ? { ...x, title: data.title || x.title, alt: data.alt || x.alt } : x)) }));
      } catch (err) {
        toast.error(formatApiError(err.response?.data?.detail) || "Descrizione foto non riuscita");
      } finally {
        setAiPhotos((a) => a.filter((id) => id !== p.id));
      }
    }
  };
  const addPhoto = (url) => {
    if (!url) return null;
    const ph = newPhoto_(url, form);
    setForm((f) => ({ ...f, photos: [...f.photos, { ...ph, order: f.photos.length, is_main: f.photos.length === 0 }] }));
    setNewPhoto("");
    return ph;
  };
  const addAndDescribe = (url) => { const ph = addPhoto(url); if (ph) describePhotos([ph]); };
  const uploadFiles = async (files) => {
    if (!files || !files.length) return;
    setUploading(true);
    const added = [];
    try {
      for (const file of Array.from(files)) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        added.push(addPhoto(data.url));
      }
      toast.success("Foto caricate: l'AI sta scrivendo titoli e testi ALT");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Errore durante il caricamento");
    } finally {
      setUploading(false);
    }
    if (added.length) describePhotos(added);
  };
  const removePhoto = (pid) => {
    let photos = form.photos.filter((p) => p.id !== pid).map((p, i) => ({ ...p, order: i }));
    if (photos.length && !photos.some((p) => p.is_main)) photos[0].is_main = true;
    set("photos", photos);
  };
  const setMain = (pid) => set("photos", form.photos.map((p) => ({ ...p, is_main: p.id === pid })));
  const movePhoto = (pid, dir) => {
    const idx = form.photos.findIndex((p) => p.id === pid);
    const j = idx + dir;
    if (j < 0 || j >= form.photos.length) return;
    const photos = [...form.photos];
    [photos[idx], photos[j]] = [photos[j], photos[idx]];
    set("photos", photos.map((p, i) => ({ ...p, order: i })));
  };
  const reorderPhotos = (from, to) => {
    if (from == null || from === to) return;
    setForm((f) => {
      const arr = [...f.photos];
      const [moved] = arr.splice(from, 1);
      arr.splice(to, 0, moved);
      return { ...f, photos: arr.map((p, i) => ({ ...p, order: i })) };
    });
    setDragIndex(null);
  };

  const save = useCallback(async (publish) => {
    if (!form.name) { toast.error("Il nome è obbligatorio"); setStep(STEPS.indexOf("Informazioni")); return null; }
    setSaving(true);
    try {
      const payload = { ...form, sqm: form.sqm === "" ? null : form.sqm, status: publish ? "pubblicata" : form.status };
      const res = isEdit ? await api.put(`/admin/properties/${id}`, payload) : await api.post("/admin/properties", payload);
      toast.success(publish ? "Struttura pubblicata! Mini-sito online." : "Struttura salvata");
      return res.data;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      return null;
    } finally {
      setSaving(false);
    }
  }, [form, id, isEdit]);

  const preview = async () => {
    const saved = await save(false);
    if (saved) window.open(`/proprieta/${saved.slug}`, "_blank");
  };

  const autoTranslate = async () => {
    if (!form.subtitle && !Object.values(form.description || {}).some(Boolean)) {
      toast.error("Aggiungi prima i contenuti in italiano");
      return;
    }
    setTranslating(true);
    try {
      const { data } = await api.post("/admin/translate", {
        target_lang: "en",
        subtitle: form.subtitle || "",
        description: form.description || {},
        nearby_names: (form.nearby_places || []).map((n) => n.name || ""),
        seo: {
          seo_title: form.seo?.seo_title || "",
          meta_description: form.seo?.meta_description || "",
          h1: form.seo?.h1 || "",
          intro_text: form.seo?.intro_text || "",
        },
      });
      setForm((f) => ({
        ...f,
        translations: {
          ...f.translations,
          en: {
            subtitle: data.subtitle || "",
            description: data.description || {},
            nearby_names: data.nearby_names || [],
            seo: data.seo || {},
          },
        },
      }));
      toast.success("Traduzione in inglese generata");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Errore durante la traduzione");
    } finally {
      setTranslating(false);
    }
  };

  const num = (k) => (e) => set(k, Number(e.target.value));
  const inputCls = "mt-1.5";

  return (
    <AdminLayout title={isEdit ? "Modifica struttura" : "Nuova casa vacanza"}>
      {/* Stepper */}
      <div className="flex items-center gap-1 mb-8 overflow-x-auto hide-scrollbar pb-2" data-testid="wizard-stepper">
        {STEPS.map((s, i) => (
          <button key={s} onClick={() => setStep(i)} className="flex items-center shrink-0">
            <span className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium transition-colors ${i === step ? "bg-brand-teal text-brand-violet" : i < step ? "bg-brand-plum text-white" : "bg-white border border-brand-plum/15 text-brand-plum/40"}`}>
              {i < step ? <Check size={15} /> : i + 1}
            </span>
            <span className={`ml-2 text-sm hidden md:inline ${i === step ? "text-brand-plum font-medium" : "text-brand-plum/40"}`}>{s}</span>
            {i < STEPS.length - 1 && <ChevronRight size={16} className="mx-2 text-brand-plum/20" />}
          </button>
        ))}
      </div>

      <div className="rounded-2xl bg-white border border-brand-plum/8 shadow-sm p-6 md:p-8 max-w-3xl" data-testid={`wizard-step-${step}`}>
        {/* Step 0: Info */}
        {cur === "Informazioni" && (
          <div className="space-y-5">
            <div><Label>Nome struttura *</Label><Input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="wizard-name" className={inputCls} placeholder="es. Villa Bellissima" /></div>
            <div><Label>Sottotitolo</Label><Input value={form.subtitle} onChange={(e) => set("subtitle", e.target.value)} className={inputCls} placeholder="Accogliente casa vacanze nel cuore di..." /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Tipologia</Label>
                <Select value={form.type} onValueChange={(v) => set("type", v)}>
                  <SelectTrigger className={inputCls}><SelectValue /></SelectTrigger>
                  <SelectContent>{["Appartamento", "Villa", "Casale", "Attico", "Chalet", "Monolocale", "B&B", "Guest House", "Residence"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label>Codice CIN</Label><Input value={form.cin} onChange={(e) => set("cin", e.target.value)} data-testid="wizard-cin" className={inputCls} placeholder="es. IT012345678" /></div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
              <div><Label>Ospiti</Label><Input type="number" min="1" value={form.guests} onChange={num("guests")} data-testid="wizard-guests" className={inputCls} /></div>
              <div><Label>Camere</Label><Input type="number" min="0" value={form.bedrooms} onChange={num("bedrooms")} className={inputCls} /></div>
              <div><Label>Letti</Label><Input type="number" min="0" value={form.beds} onChange={num("beds")} className={inputCls} /></div>
              <div><Label>Bagni</Label><Input type="number" min="0" value={form.bathrooms} onChange={num("bathrooms")} className={inputCls} /></div>
              <div><Label>Superficie (m²)</Label><Input type="number" min="0" value={form.sqm ?? ""} onChange={(e) => set("sqm", e.target.value === "" ? "" : Number(e.target.value))} data-testid="wizard-sqm" className={inputCls} placeholder="es. 85" /></div>
            </div>
            <div className="pt-2 space-y-4">
              <div className="flex items-center justify-between gap-3">
                <p className="font-serif text-lg text-brand-plum">Descrizione</p>
                <Button type="button" variant="outline" size="sm" onClick={suggestDescription} disabled={!!aiBusy} data-testid="wizard-ai-describe" className="border-brand-teal text-brand-tealDark">
                  {aiBusy === "desc" ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Sparkles size={14} className="mr-1" />} {aiBusy === "desc" ? "Sto scrivendo…" : "Rigenera con AI"}
                </Button>
              </div>
              <p className="text-xs text-brand-plum/50 -mt-2">Testi suggeriti dall'AI in base a indirizzo, luoghi vicini e servizi inseriti.</p>
              {[["presentation", "Presentazione"], ["spaces", "Gli spazi"], ["neighborhood", "Il quartiere"], ["other", "Altre informazioni"]].map(([k, l]) => (
                <div key={k}><Label>{l}</Label><Textarea rows={4} value={form.description[k]} onChange={(e) => setDesc(k, e.target.value)} data-testid={`wizard-desc-${k}`} className={inputCls} /></div>
              ))}
            </div>
          </div>
        )}

        {/* Step 1: Owner */}
        {cur === "Proprietario" && (
          <div className="space-y-5">
            <div><Label>Proprietario / Host</Label>
              <Select value={form.owner_id || ""} onValueChange={(v) => set("owner_id", v)}>
                <SelectTrigger data-testid="wizard-owner" className={inputCls}><SelectValue placeholder="Seleziona un proprietario" /></SelectTrigger>
                <SelectContent>{owners.map((o) => <SelectItem key={o.id} value={o.id}>{o.first_name} {o.last_name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {form.owner_id && (() => {
              const o = owners.find((x) => x.id === form.owner_id);
              return o ? (
                <div className="rounded-2xl bg-brand-warm p-5 flex items-center gap-4">
                  <img src={o.photo} alt="" className="w-16 h-16 rounded-full object-cover border-2 border-brand-teal" />
                  <div><p className="font-serif text-lg text-brand-plum">{o.first_name} {o.last_name}</p><p className="text-sm text-brand-plum/60">{o.email}</p>{o.phone && <p className="text-sm text-brand-plum/60">{o.phone}</p>}</div>
                </div>
              ) : null;
            })()}
            <div className="flex items-start justify-between gap-4 rounded-xl border border-brand-plum/10 p-4">
              <div>
                <Label>Mostra i contatti del proprietario sul mini-sito</Label>
                <p className="text-xs text-brand-plum/50 mt-0.5">Se attivo, numero di telefono ed email del proprietario saranno visibili ai visitatori del mini-sito.</p>
              </div>
              <Switch checked={form.show_owner_contacts} onCheckedChange={(v) => set("show_owner_contacts", v)} data-testid="wizard-show-owner-contacts" />
            </div>
            <p className="text-sm text-brand-plum/50">Puoi aggiungere nuovi proprietari dalla sezione <Link to="/admin/owners" className="text-brand-teal underline">Proprietari</Link>.</p>
          </div>
        )}

        {/* Step 2: Location */}
        {cur === "Posizione" && (
          <div className="space-y-5">
            <div><Label>Indirizzo</Label><Input value={form.address} onChange={(e) => set("address", e.target.value)} data-testid="wizard-address" className={inputCls} placeholder="es. Via Roma 10" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Città</Label><Input value={form.city} onChange={(e) => set("city", e.target.value)} onBlur={() => form.address && form.city && !aiBusy && geocode()} data-testid="wizard-city" className={inputCls} /></div>
              <div><Label>Quartiere / Zona</Label><Input value={form.neighborhood} onChange={(e) => set("neighborhood", e.target.value)} className={inputCls} /></div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div><Label>Provincia</Label><Input value={form.province} onChange={(e) => set("province", e.target.value)} className={inputCls} /></div>
              <div><Label>Regione</Label><Input value={form.region} onChange={(e) => set("region", e.target.value)} className={inputCls} /></div>
              <div><Label>CAP</Label><Input value={form.cap} onChange={(e) => set("cap", e.target.value)} className={inputCls} /></div>
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <Button type="button" onClick={geocode} disabled={!!aiBusy} data-testid="wizard-geocode-btn" className="bg-brand-teal hover:bg-brand-tealDark text-brand-violet font-semibold">
                {aiBusy === "geo" ? <Loader2 size={15} className="mr-1.5 animate-spin" /> : <MapPin size={15} className="mr-1.5" />} Rileva posizione dall'indirizzo
              </Button>
              <p className="text-xs text-brand-plum/50">Le coordinate si compilano da sole: puoi correggerle a mano se serve.</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Latitudine</Label><Input type="number" step="0.0001" value={form.lat} onChange={num("lat")} data-testid="wizard-lat" className={inputCls} /></div>
              <div><Label>Longitudine</Label><Input type="number" step="0.0001" value={form.lng} onChange={num("lng")} data-testid="wizard-lng" className={inputCls} /></div>
            </div>
            <div className="flex items-center justify-between rounded-xl border border-brand-plum/10 p-4">
              <div><Label>Posizione precisa</Label><p className="text-xs text-brand-plum/50 mt-0.5">Se disattivata, viene mostrata la zona approssimativa per privacy.</p></div>
              <Switch checked={form.show_exact_location} onCheckedChange={(v) => set("show_exact_location", v)} data-testid="wizard-exact-location" />
            </div>
            <PropertyMap lat={form.lat} lng={form.lng} exact={form.show_exact_location} label={form.name} />

            <div className="pt-4 border-t border-brand-plum/10">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <Label>Cosa vedere qui vicino</Label>
                  <p className="text-xs text-brand-plum/50 mt-0.5">Luoghi reali dalla mappa scelti dall'AI, con distanza dalla struttura.</p>
                </div>
                <div className="flex gap-2 shrink-0">
                  <Button type="button" onClick={() => findNearby()} disabled={!!aiBusy} variant="outline" size="sm" data-testid="wizard-ai-nearby" className="border-brand-teal text-brand-tealDark">
                    {aiBusy === "nearby" ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Sparkles size={14} className="mr-1" />} {aiBusy === "nearby" ? "Cerco…" : "Suggerisci luoghi"}
                  </Button>
                  <Button type="button" onClick={addNearby} variant="outline" size="sm" data-testid="wizard-add-nearby" className="border-brand-plum/15 text-brand-plum"><Plus size={14} className="mr-1" /> Aggiungi</Button>
                </div>
              </div>
              <div className="space-y-2" data-testid="wizard-nearby-list">
                {(form.nearby_places || []).map((n, i) => (
                  <div key={i} className="flex flex-col sm:flex-row gap-2 rounded-xl border border-brand-plum/10 p-2.5" data-testid={`wizard-nearby-item-${i}`}>
                    <Input value={n.name} onChange={(e) => updateNearby(i, "name", e.target.value)} placeholder="Nome luogo" className="flex-1 min-w-0 sm:min-w-[180px] h-9" />
                    <Select value={n.category} onValueChange={(v) => updateNearby(i, "category", v)}>
                      <SelectTrigger className="w-full sm:w-32 h-9"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {["attrazione", "ristorante", "spiaggia", "trasporti", "negozi", "natura"].map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Input value={n.distance} onChange={(e) => updateNearby(i, "distance", e.target.value)} placeholder="es. 5 min a piedi" className="w-full sm:w-40 h-9" />
                    <Input type="number" step="0.0001" value={n.lat ?? ""} onChange={(e) => updateNearby(i, "lat", e.target.value === "" ? null : Number(e.target.value))} placeholder="lat" className="w-full sm:w-20 h-9" />
                    <Input type="number" step="0.0001" value={n.lng ?? ""} onChange={(e) => updateNearby(i, "lng", e.target.value === "" ? null : Number(e.target.value))} placeholder="lng" className="w-full sm:w-20 h-9" />
                    <button type="button" onClick={() => removeNearby(i)} className="text-red-400 hover:text-red-500 shrink-0 self-center"><Trash2 size={16} /></button>
                  </div>
                ))}
                {(!form.nearby_places || form.nearby_places.length === 0) && <p className="text-xs text-brand-plum/40 py-2">Nessun luogo aggiunto.</p>}
              </div>
              <p className="text-xs text-brand-plum/40 mt-2">Aggiungi lat/lng per mostrare i luoghi come punti sulla mappa del mini-sito.</p>
            </div>
          </div>
        )}

        {/* Step 3: Amenities */}
        {cur === "Servizi" && (
          <div className="space-y-6">
            <div>
              <p className="text-sm text-brand-plum/60 mb-4">Seleziona i servizi disponibili. Verranno mostrati automaticamente nel mini-sito.</p>
              <div className="max-h-[460px] overflow-y-auto pr-1 space-y-5" data-testid="wizard-amenities">
                {amenitiesByCategory("it").map((group) => (
                  <div key={group.id} data-testid={`wizard-amenity-cat-${group.id}`}>
                    <p className="text-xs font-semibold uppercase tracking-wide text-brand-plum/50 mb-2.5">{group.label}</p>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {group.items.map((a) => {
                        const Icon = iconFor(a.icon);
                        const active = form.amenities.includes(a.key);
                        return (
                          <button key={a.key} onClick={() => toggleAmenity(a.key)} data-testid={`wizard-amenity-${a.key}`} className={`flex items-center gap-2.5 p-3 rounded-xl border text-sm text-left transition-all ${active ? "bg-brand-tealLight border-brand-teal text-brand-plum font-medium" : "bg-white border-brand-plum/10 text-brand-plum/60 hover:border-brand-teal"}`}>
                            <Icon size={18} className={`shrink-0 ${active ? "text-brand-tealDark" : "text-brand-plum/40"}`} /> <span className="flex-1">{a.label}</span>
                            {active && <Check size={15} className="text-brand-tealDark shrink-0" />}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="pt-5 border-t border-brand-plum/10 space-y-4" data-testid="wizard-rules">
              <p className="font-serif text-lg text-brand-plum">Regole della casa</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                <div><Label>Check-in dalle</Label><Input type="time" value={form.rules?.checkin_from || ""} onChange={(e) => setRule("checkin_from", e.target.value)} data-testid="rule-checkin-from" className={inputCls} /></div>
                <div><Label>alle</Label><Input type="time" value={form.rules?.checkin_to || ""} onChange={(e) => setRule("checkin_to", e.target.value)} data-testid="rule-checkin-to" className={inputCls} /></div>
                <div><Label>Check-out entro le</Label><Input type="time" value={form.rules?.checkout_by || ""} onChange={(e) => setRule("checkout_by", e.target.value)} data-testid="rule-checkout-by" className={inputCls} /></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {[["no_smoking", "Vietato fumare"], ["no_pets", "Animali non ammessi"], ["self_checkin", "Self check-in"], ["notify_host", "Avvisare l'host prima dell'arrivo"], ["linen_change_extra", "Cambio biancheria extra su richiesta a pagamento"], ["cleaning_extra", "Pulizia extra su richiesta a pagamento"], ["weekly_cleaning", "Pulizie e cambio biancheria settimanale inclusa"]].map(([k, l]) => {
                  const active = !!form.rules?.[k];
                  return (
                    <button key={k} type="button" onClick={() => setRule(k, !active)} data-testid={`rule-${k}`} className={`flex items-center gap-2.5 p-3 rounded-xl border text-sm text-left transition-all ${active ? "bg-brand-tealLight border-brand-teal text-brand-plum font-medium" : "bg-white border-brand-plum/10 text-brand-plum/60 hover:border-brand-teal"}`}>
                      <span className="flex-1">{l}</span>
                      {active && <Check size={15} className="text-brand-tealDark shrink-0" />}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Step 4: Photos */}
        {cur === "Foto" && (
          <div className="space-y-5">
            <label className="flex flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-brand-teal/40 bg-brand-tealLight/40 p-8 cursor-pointer hover:bg-brand-tealLight transition-colors" data-testid="wizard-upload-dropzone">
              <input type="file" accept="image/*" multiple className="hidden" data-testid="wizard-upload-input" onChange={(e) => { uploadFiles(e.target.files); e.target.value = ""; }} />
              <Upload size={26} className="text-brand-tealDark" />
              <span className="text-sm font-medium text-brand-plum">{uploading ? "Caricamento e ottimizzazione in corso..." : "Carica foto dal tuo dispositivo"}</span>
              <span className="text-xs text-brand-plum/50">JPG, PNG, WebP · ridimensionamento automatico · selezione multipla</span>
            </label>
            <div className="flex items-center gap-3 text-xs text-brand-plum/40">
              <span className="flex-1 h-px bg-brand-plum/10" /> oppure incolla un URL <span className="flex-1 h-px bg-brand-plum/10" />
            </div>
            <div className="flex gap-2">
              <Input value={newPhoto} onChange={(e) => setNewPhoto(e.target.value)} placeholder="Incolla URL immagine..." data-testid="wizard-photo-url" onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addAndDescribe(newPhoto))} />
              <Button onClick={() => addAndDescribe(newPhoto)} className="bg-brand-plum text-white shrink-0"><Plus size={16} className="mr-1" /> Aggiungi</Button>
            </div>
            <div>
              <p className="text-xs text-brand-plum/50 mb-2">Immagini suggerite (clicca per aggiungere):</p>
              <div className="flex gap-2 flex-wrap">
                {SUGGESTED_IMAGES.map((u, i) => (
                  <button key={i} onClick={() => addAndDescribe(u)} className="w-16 h-16 rounded-lg overflow-hidden border border-brand-plum/10 hover:ring-2 ring-brand-teal">
                    <img src={u} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            </div>
            {form.photos.length > 0 && (
              <div className="flex items-center justify-between gap-3 rounded-xl bg-brand-tealLight/50 p-3">
                <p className="text-xs text-brand-plum/70">L'AI scrive titolo e testo ALT di ogni foto caricata, utili per Google Immagini.</p>
                <Button type="button" variant="outline" size="sm" disabled={aiPhotos.length > 0} onClick={() => describePhotos(form.photos)} data-testid="wizard-ai-photos-all" className="border-brand-teal text-brand-tealDark shrink-0">
                  {aiPhotos.length > 0 ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Sparkles size={14} className="mr-1" />} {aiPhotos.length > 0 ? `Descrivo… (${aiPhotos.length})` : "Descrivi tutte con AI"}
                </Button>
              </div>
            )}
            <div className="space-y-2" data-testid="wizard-photo-list">
              {form.photos.map((p, i) => (
                <div key={p.id} draggable onDragStart={() => setDragIndex(i)} onDragOver={(e) => e.preventDefault()} onDrop={() => reorderPhotos(dragIndex, i)} onDragEnd={() => setDragIndex(null)} className={`flex items-center gap-3 rounded-xl border p-2.5 bg-white cursor-move transition-colors ${dragIndex === i ? "border-brand-teal ring-2 ring-brand-teal/30" : "border-brand-plum/10"}`} data-testid={`wizard-photo-item-${i}`}>
                  <GripVertical size={16} className="text-brand-plum/30 shrink-0" />
                  <div className="flex flex-col text-brand-plum/30">
                    <button onClick={() => movePhoto(p.id, -1)} disabled={i === 0} className="disabled:opacity-30"><ChevronLeft size={14} className="rotate-90" /></button>
                    <button onClick={() => movePhoto(p.id, 1)} disabled={i === form.photos.length - 1} className="disabled:opacity-30"><ChevronRight size={14} className="rotate-90" /></button>
                  </div>
                  <img src={p.url} alt="" className="w-16 h-16 rounded-lg object-cover" />
                  <div className="flex-1 min-w-0 space-y-1.5">
                    <Input value={p.title || ""} onChange={(e) => set("photos", form.photos.map((x) => x.id === p.id ? { ...x, title: e.target.value } : x))} placeholder="Titolo foto" data-testid={`wizard-photo-title-${i}`} className="h-8 text-sm" />
                    <Input value={p.alt || ""} onChange={(e) => set("photos", form.photos.map((x) => x.id === p.id ? { ...x, alt: e.target.value } : x))} placeholder="Testo ALT (descrizione per Google)" data-testid={`wizard-photo-alt-${i}`} className="h-8 text-xs" />
                  </div>
                  <button onClick={() => describePhotos([p])} disabled={aiPhotos.includes(p.id)} title="Descrivi con AI" data-testid={`wizard-photo-ai-${i}`} className="shrink-0 text-brand-tealDark hover:text-brand-teal disabled:opacity-60">
                    {aiPhotos.includes(p.id) ? <Loader2 size={17} className="animate-spin" /> : <Sparkles size={17} />}
                  </button>
                  <button onClick={() => setMain(p.id)} title="Imposta come principale" className={`shrink-0 ${p.is_main ? "text-brand-gold" : "text-brand-plum/30 hover:text-brand-gold"}`}><Star size={18} className={p.is_main ? "fill-brand-gold" : ""} /></button>
                  <button onClick={() => removePhoto(p.id)} className="text-red-400 hover:text-red-500 shrink-0"><Trash2 size={16} /></button>
                </div>
              ))}
              {form.photos.length === 0 && <div className="rounded-xl border border-dashed border-brand-plum/20 p-8 text-center text-brand-plum/40"><Upload size={24} className="mx-auto mb-2" />Nessuna foto. Aggiungine almeno una.</div>}
            </div>
          </div>
        )}

        {/* Step 5: Booking */}
        {cur === "Booking" && (
          <div className="space-y-5">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div><Label>Prezzo/notte (€)</Label><Input type="number" value={form.price_per_night} onChange={num("price_per_night")} className={inputCls} /></div>
              <div><Label>Pulizie (€)</Label><Input type="number" value={form.cleaning_fee} onChange={num("cleaning_fee")} className={inputCls} /></div>
              <div><Label>Min. notti</Label><Input type="number" value={form.min_stay} onChange={num("min_stay")} className={inputCls} /></div>
              <div><Label>Sconto diretto (%)</Label><Input type="number" value={form.discount_percent} onChange={num("discount_percent")} className={inputCls} /></div>
            </div>
            <div>
              <Label>Link sistema di prenotazione</Label>
              <Input value={form.booking_engine.url} onChange={(e) => setBE("url", e.target.value)} data-testid="wizard-be-url" className={inputCls} placeholder="https://book.octorate.com/..." />
              <p className="text-xs text-brand-plum/40 mt-1">Il pulsante "Verifica disponibilità" del mini-sito aprirà questo link.</p>
            </div>
          </div>
        )}

        {/* Step 7: Translations + publish */}
        {cur === "Traduzioni & Pubblica" && (
          <div className="space-y-5" data-testid="wizard-translations">
            <p className="text-sm text-brand-plum/60">Ultimo passaggio: genera la versione inglese di tutti i testi (SEO, H1, descrizioni) con un clic, poi pubblica. I campi lasciati vuoti mostreranno l'italiano.</p>
            <div className="flex flex-wrap items-center gap-3">
              <span className="px-4 py-1.5 rounded-full text-sm border bg-brand-teal border-brand-teal text-brand-violet font-medium">English</span>
              <Button type="button" onClick={autoTranslate} disabled={translating} data-testid="wizard-auto-translate-btn" className="bg-brand-plum text-white">
                <Languages size={16} className="mr-1.5" /> {translating ? "Traduzione in corso..." : "Traduci in inglese"}
              </Button>
            </div>
            <div><Label>Sottotitolo ({transLang.toUpperCase()})</Label>
              <Input value={getTr(transLang).subtitle || ""} onChange={(e) => setTr(transLang, "subtitle", e.target.value)} data-testid="trans-subtitle" className={inputCls} placeholder={form.subtitle} /></div>
            {[["presentation", "Presentazione"], ["spaces", "Gli spazi"], ["neighborhood", "Il quartiere"], ["other", "Altre informazioni"]].map(([k, l]) => (
              <div key={k}><Label>{l} ({transLang.toUpperCase()})</Label>
                <Textarea rows={2} value={getTr(transLang).description?.[k] || ""} onChange={(e) => setTrDesc(transLang, k, e.target.value)} className={inputCls} placeholder={form.description?.[k] || ""} /></div>
            ))}
            {(form.nearby_places || []).length > 0 && (
              <div>
                <Label>Nomi luoghi "qui vicino" ({transLang.toUpperCase()})</Label>
                <div className="space-y-2 mt-1.5">
                  {form.nearby_places.map((n, i) => (
                    <Input key={i} value={getTr(transLang).nearby_names?.[i] || ""} onChange={(e) => setTrNearby(transLang, i, e.target.value)} placeholder={n.name} className="h-9" data-testid={`trans-nearby-${i}`} />
                  ))}
                </div>
              </div>
            )}
            <div className="pt-4 border-t border-brand-plum/10 space-y-4">
              <p className="font-serif text-lg text-brand-plum">SEO ({transLang.toUpperCase()})</p>
              <div><Label>SEO Title ({transLang.toUpperCase()})</Label>
                <Input value={getTr(transLang).seo?.seo_title || ""} onChange={(e) => setTrSeo(transLang, "seo_title", e.target.value)} data-testid="trans-seo-title" className={inputCls} placeholder={form.seo?.seo_title || ""} /></div>
              <div><Label>Meta Description ({transLang.toUpperCase()})</Label>
                <Textarea rows={2} value={getTr(transLang).seo?.meta_description || ""} onChange={(e) => setTrSeo(transLang, "meta_description", e.target.value)} data-testid="trans-seo-meta" className={inputCls} placeholder={form.seo?.meta_description || ""} /></div>
              <div><Label>H1 ({transLang.toUpperCase()})</Label>
                <Input value={getTr(transLang).seo?.h1 || ""} onChange={(e) => setTrSeo(transLang, "h1", e.target.value)} data-testid="trans-seo-h1" className={inputCls} placeholder={form.seo?.h1 || ""} /></div>
              <div><Label>Testo introduttivo ({transLang.toUpperCase()})</Label>
                <Textarea rows={2} value={getTr(transLang).seo?.intro_text || ""} onChange={(e) => setTrSeo(transLang, "intro_text", e.target.value)} data-testid="trans-seo-intro" className={inputCls} placeholder={form.seo?.intro_text || ""} /></div>
            </div>
            <div className="flex items-center justify-between rounded-xl border border-brand-plum/10 p-4">
              <div><Label>Stato pubblicazione</Label><p className="text-xs text-brand-plum/50 mt-0.5">Solo le strutture pubblicate appaiono sul portale.</p></div>
              <Select value={form.status} onValueChange={(v) => set("status", v)}>
                <SelectTrigger className="w-[160px]"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="bozza">Bozza</SelectItem>
                  <SelectItem value="pubblicata">Pubblicata</SelectItem>
                  <SelectItem value="non_pubblicata">Non pubblicata</SelectItem>
                  <SelectItem value="sospesa">Sospesa</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex flex-wrap gap-3 pt-2">
              <Button onClick={preview} variant="outline" disabled={saving} data-testid="wizard-preview-btn" className="border-brand-plum/20 text-brand-plum"><Eye size={16} className="mr-1.5" /> Anteprima mini-sito</Button>
              <Button onClick={async () => { const s = await save(false); if (s) navigate("/admin/properties"); }} disabled={saving} data-testid="admin-wizard-save-property-btn" className="bg-brand-plum text-white">Salva bozza</Button>
              <Button onClick={async () => { const s = await save(true); if (s) navigate("/admin/properties"); }} disabled={saving} data-testid="wizard-publish-btn" className="bg-brand-teal hover:bg-brand-tealDark text-brand-violet font-semibold">Pubblica mini-sito</Button>
            </div>
          </div>
        )}

        {/* Step 6: SEO */}
        {cur === "SEO" && (
          <div className="space-y-5">
            <div className="flex items-center justify-between gap-3 rounded-xl bg-brand-tealLight/50 p-3">
              <p className="text-xs text-brand-plum/70">Campi suggeriti dall'AI per il miglior posizionamento, in base ai dati inseriti.</p>
              <Button type="button" variant="outline" size="sm" onClick={suggestSeo} disabled={!!aiBusy} data-testid="wizard-ai-seo" className="border-brand-teal text-brand-tealDark shrink-0">
                {aiBusy === "seo" ? <Loader2 size={14} className="mr-1 animate-spin" /> : <Sparkles size={14} className="mr-1" />} {aiBusy === "seo" ? "Sto scrivendo…" : "Rigenera con AI"}
              </Button>
            </div>
            <div><Label>URL Slug</Label><Input value={form.seo.slug ?? form.slug ?? ""} onChange={(e) => set("slug", e.target.value)} data-testid="wizard-slug" className={inputCls} placeholder="villa-bellissima-chianti" />
              <p className="text-xs text-brand-plum/40 mt-1 font-mono">quattro.house/proprieta/{form.slug || "..."}</p>
            </div>
            <div><Label>SEO Title <span className="text-brand-plum/40 text-xs">({(form.seo.seo_title || "").length}/60)</span></Label><Input value={form.seo.seo_title} onChange={(e) => setSeo("seo_title", e.target.value)} data-testid="wizard-seo-title" className={inputCls} /></div>
            <div><Label>Meta Description <span className="text-brand-plum/40 text-xs">({(form.seo.meta_description || "").length}/155)</span></Label><Textarea rows={2} value={form.seo.meta_description} onChange={(e) => setSeo("meta_description", e.target.value)} data-testid="wizard-seo-meta" className={inputCls} /></div>
            <div><Label>H1</Label><Input value={form.seo.h1} onChange={(e) => setSeo("h1", e.target.value)} data-testid="wizard-seo-h1" className={inputCls} /></div>
            <div><Label>Testo introduttivo</Label><Textarea rows={3} value={form.seo.intro_text} onChange={(e) => setSeo("intro_text", e.target.value)} data-testid="wizard-seo-intro" className={inputCls} /></div>
            <p className="text-sm text-brand-plum/50 pt-2 border-t border-brand-plum/10">Compila i testi SEO in italiano. Nel passaggio successivo li tradurrai in inglese con l'AI prima di pubblicare.</p>
          </div>
        )}
      </div>

      {/* Nav */}
      <div className="flex justify-between max-w-3xl mt-6">
        <Button onClick={() => setStep((s) => Math.max(0, s - 1))} disabled={step === 0} variant="outline" className="border-brand-plum/20 text-brand-plum"><ChevronLeft size={16} className="mr-1" /> Indietro</Button>
        {step < STEPS.length - 1 ? (
          <Button onClick={() => setStep((s) => Math.min(STEPS.length - 1, s + 1))} data-testid="admin-wizard-next-step-btn" className="bg-brand-plum text-white">Avanti <ChevronRight size={16} className="ml-1" /></Button>
        ) : null}
      </div>
    </AdminLayout>
  );
}
